import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NovasellLogo } from '@/components/NovasellLogo';
import { HalfModal } from '@/components/halfmodal/HalfModal';
import { GlobalFooter } from '@/components/GlobalFooter';
import { SampleProposalModal } from '@/components/SampleProposalModal';
import { proposalSlidePairs } from '@/constants/proposalSlides';
import { useDrawerStore } from '@/stores/drawerStore';
import { useAuthStore } from '@/stores/authStore';
import { validateWebsiteUrl } from '@/schemas/websiteSchema';
import { trackEvent } from '@/utils/analytics';

// ローカルアセット画像
import heroBackground from '@/assets/80c2d4ecefb35aa22098c49784a2d6540e2b410d.png';

/**
 * LandingPage
 * NOVASELL ONE のランディングページ
 * Figma デザインに基づいて実装
 */
export function LandingPage() {
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const { openDrawer, setFormData } = useDrawerStore();
  const { isAuthenticated, user } = useAuthStore();

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % proposalSlidePairs.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + proposalSlidePairs.length) % proposalSlidePairs.length);
  };

  const openSampleModal = () => {
    trackEvent('sample_modal_open', { location: 'proposal_section' });
    setIsSampleModalOpen(true);
  };

  /**
   * 認証状態をチェックして適切な Drawer を開く
   * - 認証済み → フォーム画面を表示
   * - 未認証 → 認証モーダルを表示
   */
  const openDrawerWithAuthCheck = async (): Promise<void> => {
    setIsCheckingAuth(true);
    try {
      // 認証状態を最新に更新してから判定する
      // ページロード時の initializeAuth() が完了していない可能性や、
      // Cookie の状態と Zustand の状態が乖離している可能性に対応
      const authStore = useAuthStore.getState();
      const authResult = await authStore.initializeAuth();

      if (authResult.success) {
        // ログイン済み → フォーム画面を直接表示（ユーザー情報は自動入力される）
        openDrawer('form');
      } else {
        // 未ログイン → 認証モーダルを表示
        openDrawer('auth');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // エラー時は認証モーダルを表示（安全側に倒す）
      openDrawer('auth');
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // URL 入力時のハンドラ（エラーをクリア）
  const handleUrlChange = (value: string) => {
    setWebsiteUrl(value);
    // 入力中はエラーをクリア
    if (urlError) {
      setUrlError(null);
    }
  };

  /**
   * 「無料提案を依頼する」ボタンのクリックハンドラ
   * - URL をバリデーション
   * - 認証状態を最新に更新
   * - 認証済みならフォーム画面、未認証なら認証モーダルを表示
   */
  const handleOpenModal = async () => {
    // URL バリデーション
    const validationResult = validateWebsiteUrl(websiteUrl);

    if (!validationResult.success) {
      setUrlError(validationResult.error);
      return;
    }

    // GA4 イベント送信: ヒーローセクションの CTA クリック
    trackEvent('cta_click', {
      location: 'hero',
      website_url: validationResult.data,
    });

    // バリデーション成功: 正規化された URL を formData にセット
    setFormData({ websiteUrl: validationResult.data });
    setUrlError(null);

    await openDrawerWithAuthCheck();
  };

  return (
    <div
      className="min-h-screen bg-white"
      style={{ fontFamily: '"Noto Sans JP", "Inter", sans-serif' }}
    >
      {/* Hero Section with Background */}
      <section
        className="relative w-full min-h-[450px] md:min-h-[550px] lg:min-h-[600px]"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Header */}
        <header className="relative z-10 bg-white border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <NovasellLogo />
              <nav className="hidden md:flex items-center gap-8">
                {isAuthenticated && user && (
                  <span
                    className="text-[#6B7280]"
                    style={{ fontSize: '14px', fontWeight: 500 }}
                  >
                    {user.email}
                  </span>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-20">
          <div className="max-w-2xl">
            <div className="space-y-4 sm:space-y-6">
              <div className="space-y-3 sm:space-y-4">
                <h1 className="text-[#002060]">
                  <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium leading-snug">
                    すぐに
                    <span className="relative inline-block">
                      <span className="relative z-10 font-bold px-[2px] py-[0px] text-2xl sm:text-3xl md:text-4xl lg:text-[40px]">本気の提案</span>
                      <span className="absolute bottom-0.5 sm:bottom-1 left-0 w-full h-2 sm:h-3 bg-[#0052FF] opacity-30 z-0" />
                    </span>
                    が届く
                  </div>
                  <div className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mt-1 sm:mt-2">
                    <span className="relative inline-block">
                      <span className="relative z-10">プロの広告戦略</span>
                      <span className="absolute bottom-1 sm:bottom-2 left-0 w-full h-2 sm:h-4 bg-[#0052FF] opacity-30 z-0" />
                    </span>
                    を
                    <span className="relative inline-block">
                      <span className="relative z-10">
                        <span className="relative inline-block">
                          無
                          <span className="absolute -top-1.5 sm:-top-3 left-1/2 -translate-x-1/2 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#0052FF] rounded-full" />
                        </span>
                        <span className="relative inline-block">
                          料
                          <span className="absolute -top-1.5 sm:-top-3 left-1/2 -translate-x-1/2 w-1.5 sm:w-2 h-1.5 sm:h-2 bg-[#0052FF] rounded-full" />
                        </span>
                        で
                      </span>
                    </span>
                  </div>
                </h1>
                <p className="text-[#002060] text-sm sm:text-base md:text-lg lg:text-xl font-medium leading-relaxed">
                  あなたのサイトURLを入力するだけ。<br className="sm:hidden" />もう、広告運用で悩みません。
                </p>
              </div>

              {/* Input Form and CTA */}
              <div className="flex flex-col gap-2 sm:gap-3 pt-2 max-w-full sm:max-w-[400px]">
                <div className="flex flex-col gap-2">
                  <label htmlFor="hero-website-url" className="sr-only">
                    ウェブサイトのURL
                  </label>
                  <input
                    id="hero-website-url"
                    type="text"
                    value={websiteUrl}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleOpenModal();
                      }
                    }}
                    placeholder="https://www.example.com"
                    aria-invalid={urlError ? 'true' : 'false'}
                    aria-describedby={urlError ? 'hero-url-error' : undefined}
                    className={`flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-white border-2 rounded-lg text-[#111827] placeholder:text-[#9CA3AF] shadow-lg transition-colors text-sm sm:text-base ${
                      urlError
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-[#E5E7EB] focus:border-[#0052FF]'
                    } focus:outline-none`}
                  />
                  {urlError && (
                    <p
                      id="hero-url-error"
                      role="alert"
                      className="text-red-600 bg-white/90 px-3 py-2 rounded-lg mt-1 text-xs sm:text-sm font-medium"
                    >
                      {urlError}
                    </p>
                  )}
                </div>
                <Button
                  onClick={handleOpenModal}
                  disabled={isCheckingAuth}
                  className="w-full bg-[#0052FF] hover:bg-[#0041CC] text-white px-6 sm:px-10 py-3 sm:py-5 h-auto rounded-lg transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base lg:text-lg font-bold"
                >
                  {isCheckingAuth ? '確認中...' : '無料で提案資料を受け取る'}
                  {!isCheckingAuth && (
                    <ChevronRight className="ml-1 sm:ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proposal Sample Section (Figma Make v3 移植: カルーセル型) */}
      <section className="bg-white py-20 lg:py-24 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          {/* Header & Text */}
          <div className="text-center w-full max-w-[1200px] mx-auto mb-16 px-4">
            <h2
              className="text-[#002060] font-bold tracking-tight mb-8 lg:whitespace-nowrap text-xl sm:text-2xl md:text-3xl lg:text-[34px] leading-snug"
            >
              代理店が数週間かける50枚の戦略書。30分で無料提供。
            </h2>
            <div className="max-w-[800px] mx-auto">
              <p className="text-[#6B7280] text-left text-base sm:text-lg lg:text-xl leading-relaxed">
                通常は高額なコンサル料と数週間を要する「約50枚の網羅的提案」を、わずか30分で無料作成します。3C分析やペルソナ設計、即戦力のクリエイティブ案、メディア配分まで網羅。ラクスルを売上300億円へ導いた勝てるロジックを凝縮しました。代理店の提案に満足できない方へ、出し惜しみなしの「正解」を届けます。
              </p>
            </div>
          </div>

          {/* Carousel */}
          <div className="relative w-full max-w-[1200px] mx-auto">
            <div className="relative h-[400px] sm:h-[500px] lg:h-[600px] flex items-center justify-center overflow-hidden">
              {proposalSlidePairs.map((pair, idx) => {
                const offset = (idx - currentSlide + proposalSlidePairs.length) % proposalSlidePairs.length;
                const baseClass =
                  'absolute transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] flex flex-col bg-white rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] cursor-pointer overflow-hidden';
                const slideSizeClass = 'w-[280px] sm:w-[360px] lg:w-[460px] aspect-[16/18]';

                let positionClass = '';
                if (offset === 0) {
                  positionClass =
                    'translate-x-0 scale-100 opacity-100 z-20 border-2 border-[#10B981] hover:scale-[1.02] shadow-[0_20px_40px_rgb(0,0,0,0.15)]';
                } else if (offset === 1 || offset === proposalSlidePairs.length - 1) {
                  const direction =
                    offset === 1
                      ? 'translate-x-[65%] sm:translate-x-[75%] lg:translate-x-[85%]'
                      : '-translate-x-[65%] sm:-translate-x-[75%] lg:-translate-x-[85%]';
                  positionClass = `${direction} scale-[0.85] opacity-50 z-10 hover:opacity-80`;
                } else {
                  positionClass = 'translate-x-0 scale-75 opacity-0 z-0 pointer-events-none';
                }

                return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (offset === 0) {
                        openSampleModal();
                      } else if (offset === 1) {
                        handleNextSlide();
                      } else if (offset === proposalSlidePairs.length - 1) {
                        handlePrevSlide();
                      }
                    }}
                    className={`${baseClass} ${slideSizeClass} ${positionClass}`}
                  >
                    <img
                      src={pair[0]}
                      alt={`提案資料 スライド ${idx * 2 + 1}`}
                      className="w-full h-1/2 object-cover border-b border-gray-100"
                    />
                    <img
                      src={pair[1]}
                      alt={`提案資料 スライド ${idx * 2 + 2}`}
                      className="w-full h-1/2 object-cover"
                    />
                  </div>
                );
              })}

              {/* Navigation Arrows */}
              <button
                onMouseEnter={handlePrevSlide}
                onClick={handlePrevSlide}
                className="absolute left-2 sm:left-8 lg:left-12 z-30 p-3 bg-white hover:bg-gray-50 border border-gray-200 rounded-full shadow-lg text-[#0264BF] transition-all hover:scale-110"
                aria-label="前のスライド"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onMouseEnter={handleNextSlide}
                onClick={handleNextSlide}
                className="absolute right-2 sm:right-8 lg:right-12 z-30 p-3 bg-white hover:bg-gray-50 border border-gray-200 rounded-full shadow-lg text-[#0264BF] transition-all hover:scale-110"
                aria-label="次のスライド"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Dots */}
            <div className="flex justify-center items-center gap-3 -mt-6 relative z-30">
              {proposalSlidePairs.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    currentSlide === idx ? 'bg-[#0264BF] w-8' : 'bg-gray-300 w-2.5 hover:bg-gray-400'
                  }`}
                  aria-label={`スライド ${idx + 1} に移動`}
                />
              ))}
            </div>

            {/* CTA Button */}
            <div className="flex justify-center mt-12">
              <Button
                onClick={openSampleModal}
                className="w-full max-w-[400px] bg-[#0264BF] hover:bg-[#01508A] text-white px-10 py-5 h-auto rounded-lg transition-all shadow-xl flex items-center justify-center group text-base sm:text-lg font-bold"
              >
                提案書のサンプルを見る
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <GlobalFooter />

      {/* Half Modal */}
      <HalfModal />

      {/* Sample Proposal Modal */}
      {isSampleModalOpen && (
        <SampleProposalModal
          onClose={() => setIsSampleModalOpen(false)}
          onOpenContactModal={async () => {
            setIsSampleModalOpen(false);
            await openDrawerWithAuthCheck();
          }}
        />
      )}
    </div>
  );
}
